import { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { DocumentEditorContainerComponent, Toolbar } from '@syncfusion/ej2-react-documenteditor';
import { DocumentEditorProps } from '../types/documentEditor';
import { useStateContext } from '../contexts/ContextProvider';
import './DocumentEditor.css';

DocumentEditorContainerComponent.Inject(Toolbar);

const DocumentEditor = forwardRef<any, DocumentEditorProps>(({ 
    height = '590px', 
    onSave, 
    template,
    isReadOnly = false,
    onEditToggle,
    initialContent,
    onChange,
    enableToolbar = true,
    showPropertiesPane = true
}, ref) => {
    const container = useRef<DocumentEditorContainerComponent>(null);
    const contentLoadedRef = useRef(false);
    const hostUrl = "https://document.syncfusion.com/web-services/docx-editor/api/documenteditor/";
    const { currentMode } = useStateContext();
    
    // Expose the container to parent via ref
    useImperativeHandle(ref, () => ({
        documentEditor: container.current?.documentEditor,
        insertText: (text: string) => {
            if (container.current?.documentEditor) {
                container.current.documentEditor.editor.insertText(text);
                handleContentChange();
            }
        }
    }));
    
    // Handle content changes
    const handleContentChange = () => {
        if (onChange && container.current?.documentEditor) {
            try {
                const sfdt = container.current.documentEditor.serialize();
                onChange(sfdt);
            } catch (error) {
                console.error('Error serializing document:', error);
            }
        }
    };
    
    // Handle theme changes
    useEffect(() => {
        const isDark = currentMode === 'Dark';
        const themeId = 'syncfusion-dark-theme';
        
        console.log('[DocumentEditor] Theme change detected:', { currentMode, isDark });
        
        // Remove existing dark theme link if present
        const existingLink = document.getElementById(themeId);
        if (existingLink) {
            console.log('[DocumentEditor] Removing existing dark theme link');
            existingLink.remove();
        }
        
        // Add or remove dark theme
        if (isDark) {
            console.log('[DocumentEditor] Applying dark theme');
            // Use CDN for dark theme to override light theme
            const link = document.createElement('link');
            link.id = themeId;
            link.rel = 'stylesheet';
            link.href = 'https://cdn.syncfusion.com/ej2/27.1.48/tailwind-dark.css';
            document.head.appendChild(link);
            
            document.body.classList.add('e-dark-mode');
        } else {
            console.log('[DocumentEditor] Applying light theme');
            document.body.classList.remove('e-dark-mode');
        }
        
        // Don't refresh the editor - it causes issues with editing
        // The theme change will apply automatically via CSS
        
        return () => {
            // Cleanup on unmount
            const link = document.getElementById(themeId);
            if (link) link.remove();
        };
    }, [currentMode]);
    
    // Initial setup - runs once on mount
    useEffect(() => {
        if (!container.current) return;
        
        const editor = container.current.documentEditor;
        if (!editor) return;
        
        // Set read-only state
        editor.isReadOnly = isReadOnly;
        
        // Set default document name
        editor.documentName = "Clinical Document";
        
        // Configure editor settings
        editor.pageOutline = "#E0E0E0";
        editor.acceptTab = true;
        
        // Enable rulers (both horizontal and vertical)
        editor.documentEditorSettings.showRuler = true;
        
        // Enable ruler helper for easier margin adjustment
        editor.documentEditorSettings.rulerHelper = true;
        
        // Enable table resizer for dragging table borders/columns/rows
        editor.enableTableResizer = true;
        
        // Set layout type to print (removes web layout button)
        editor.layoutType = 'Pages';
        
        // Setup document change handler
        container.current.documentChange = (): void => {
            if (container.current) {
                container.current.documentEditor.focusIn();
            }
        };
        
        // Track content changes
        if (onChange) {
            editor.contentChange = () => {
                handleContentChange();
            };
        }
        
        // Hook into the toolbar save button click
        if (container.current.toolbarModule) {
            const toolbarClick = (args: any): void => {
                if (args.item && args.item.id === 'save') {
                    handleSave();
                }
            };
            container.current.toolbarClick = toolbarClick;
            
            // Hide the web layout button from the status bar
            const statusBar = document.querySelector('.e-de-statusbar-web-layout');
            if (statusBar) {
                (statusBar as HTMLElement).style.display = 'none';
            }
        }
        
        // Resize editor to fit container
        editor.resize();
        
    }, [isReadOnly]);
    
    // Content loading - runs ONCE when component mounts
    useEffect(() => {
        if (!container.current || contentLoadedRef.current) return;
        
        const editor = container.current.documentEditor;
        if (!editor) return;
        
        // Load initial content if provided (SFDT format)
        if (initialContent) {
            // Small delay to ensure DOM is fully ready when switching views
            const timer = setTimeout(() => {
                try {
                    console.log('[DocumentEditor] Loading initial content');
                    editor.open(initialContent);
                    contentLoadedRef.current = true;
                } catch (error) {
                    console.error('Error loading document content:', error);
                }
            }, 50);
            
            return () => clearTimeout(timer);
        } else if (template) {
            // Load template if provided
            try {
                editor.open(JSON.stringify(template));
                contentLoadedRef.current = true;
            } catch (error) {
                console.error('Error loading template:', error);
            }
        } else {
            contentLoadedRef.current = true;
        }
        
    }, []); // Empty dependency array - only run once on mount
    
    const handleSave = async (): Promise<void> => {
        if (!container.current) return;
        
        container.current.documentEditor.saveAsBlob('Docx').then((blob: Blob) => {
            if (onSave) {
                onSave(blob);
            }
        });
    };
    
    // Handle drag and drop
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'copy';
    };
    
    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        
        const text = e.dataTransfer.getData('text/plain');
        if (text && container.current?.documentEditor) {
            // Insert text at current cursor position
            container.current.documentEditor.editor.insertText(text);
            handleContentChange();
        }
    };
    
    return (
        <div className="control-pane h-full" onDragOver={handleDragOver} onDrop={handleDrop}>
            <div className="control-section h-full">
                <div id="documenteditor_container_body" className="h-full">
                    <DocumentEditorContainerComponent
                        id="container"
                        ref={container}
                        style={{ display: "block" }}
                        height={height}
                        serviceUrl={hostUrl}
                        enableToolbar={enableToolbar}
                        showPropertiesPane={showPropertiesPane}
                        locale="en-US"
                    />
                </div>
            </div>
        </div>
    );
});

DocumentEditor.displayName = 'DocumentEditor';

export default DocumentEditor;